/**
 * Authentication utilities for token validation and access control
 */

import { selectIsAuthenticated, selectToken } from '@/shared/store/auth.store'

/**
 * Decodes a JWT token without verification
 * @param token - JWT token string
 * @returns Decoded payload or null if invalid
 */
function decodeJWT(token: string): { exp?: number } | null {
    try {
        const parts = token.split('.')
        if (parts.length !== 3) {
            return null
        }

        // Decode the payload (second part)
        const payload = parts[1]
        if (!payload) {
            return null
        }
        const decodedPayload = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
        return JSON.parse(decodedPayload)
    } catch (error) {
        console.error('Failed to decode JWT token:', error)
        return null
    }
}

/**
 * Checks if a JWT token is expired
 * @param token - JWT token string
 * @returns true if token is expired or invalid
 */
export function isTokenExpired(token: string | null): boolean {
    if (!token) {
        return true
    }
    const decoded = decodeJWT(token)

    if (!decoded || !decoded.exp) {
        return false
    }

    // JWT exp is in seconds, Date.now() is in milliseconds
    const currentTime = Math.floor(Date.now() / 1000)

    return decoded.exp < currentTime
}

/**
 * Validates the current authentication state
 * @returns true if user is authenticated with valid token
 */
export function isAuthenticated(): boolean {
    const isAuth = selectIsAuthenticated()

    const token = selectToken()

    if (!isAuth || !token) {
        return false
    }

    // Check if token is expired
    if (isTokenExpired(token)) {
        return false
    }

    return true
}

/**
 * List of public routes that don't require authentication
 */
export const PUBLIC_ROUTES = ['/login', '/auth/callback', '/404', '/maintenance', '/users/invite', '/blocked']

/**
 * Checks if a route is public
 * @param pathname - Route pathname
 * @returns true if route is public
 */
export function isPublicRoute(pathname: string): boolean {
    return PUBLIC_ROUTES.some(route => pathname === route || pathname.startsWith(`${route}/`))
}
